/* @jsx h */
import { createCustomerCheckout } from "@app/server/client";
import { useTRPC } from "@app/vue/providers/index";
import { useMutation } from "@tanstack/vue-query";
import { useMenu } from "@app/vue/providers/index";
import { pp } from "@lemmonhq/blogic";
import { defineComponent, ref, h, computed } from "vue";
import { RouterLink, useRouter } from "vue-router";
import {
  BillSection,
  PaymentSection,
  DeliverySection,
  NotesSection,
  CartItems,
  PickupDetailsSection,
  TipsSection,
  ScheduleSection,
} from "./components";
import {
  calculateCartSubtotal,
  CartAPI,
  getTodayInTimezone,
} from "@lemmonhq/orders";
import { LegalDisclamair } from "../../Shop/components";
import { getDeliveryCities } from "@lemmonhq/blogic";
import type { VacationSchedule } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

export type CheckoutSheetProps = {
  isOpen: boolean;
  onClose: (t: string) => void;
};

export const CheckoutSheet = defineComponent({
  props: ["isOpen", "onClose"],
  setup(props) {
    const router = useRouter();
    const trpc = useTRPC();
    const { t } = useLocalization();
    const { shop, cart, realtime, groupOrder } = useMenu();
    shop.value.timezone;
    const isLoading = ref(false);
    const createIntegratedWorkflowMutation = useMutation({
      mutationFn: (checkoutId: string) =>
        trpc.integratedCustomerPaymentWorkflow.create.mutate({
          subjectType: "group_order",
          checkoutId,
        }),
    });
    const rawVacationSchedule = computed(() => {
      const settings = shop.value?.settings;
      if (!settings) return { items: [] } as VacationSchedule;
      const raw = settings.find(
        (setting) => setting.key === "vacation_schedule",
      )?.value;
      if (!raw) return { items: [] } as VacationSchedule;
      if (typeof raw === "object" && raw !== null) {
        return raw as VacationSchedule;
      }
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw) as VacationSchedule;
        } catch {
          return { items: [] } as VacationSchedule;
        }
      }
      return { items: [] } as VacationSchedule;
    });
    // closed_range items block reservations only — takeaway/delivery ordering
    // ignores them, so drop them before the per-date `.find()` lookups below.
    const vacationSchedule = computed(
      (): VacationSchedule => ({
        items: (rawVacationSchedule.value?.items ?? []).filter(
          (item) => item?.payload?.type !== "closed_range",
        ),
      }),
    );
    const toLocalDateString = (value: Date) => {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const getOrderDateTime = () => {
      if (cart.cart.schedule?.date && cart.cart.schedule?.time) {
        return {
          date: cart.cart.schedule.date,
          time: cart.cart.schedule.time,
        };
      }
      // Use restaurant timezone instead of user's local timezone
      const timezone = shop.value.timezone || "UTC";
      const now = getTodayInTimezone(timezone);
      return {
        date: toLocalDateString(now),
        time: now.toTimeString().slice(0, 5),
      };
    };

    const normalizeDateString = (value: string) => value.split("T")[0];

    const vacationOverride = computed(() => {
      const serviceMethod = cart.cart.serviceMethod;
      if (serviceMethod !== "takeaway" && serviceMethod !== "delivery") {
        return null;
      }
      const { date } = getOrderDateTime();
      const normalizedDate = normalizeDateString(date);
      return (
        vacationSchedule.value.items.find(
          (item) => normalizeDateString(item.date) === normalizedDate,
        ) || null
      );
    });

    const isVacationClosed = computed(() => {
      if (!vacationOverride.value) return false;
      if (vacationOverride.value.payload.type === "closed_all_day") {
        return true;
      }
      if (vacationOverride.value.payload.type === "intervals") {
        const { time } = getOrderDateTime();
        const isWithinInterval = vacationOverride.value.payload.intervals.some(
          (interval) => time >= interval.start && time < interval.end,
        );
        return !isWithinInterval;
      }
      return false;
    });

    const getRestaurantSetting = (key: string): string | undefined => {
      const setting = shop.value?.settings?.find((s) => s.key === key);
      return setting?.value as string | undefined;
    };

    const getNextOpeningTime = () => {
      const serviceMethod = cart.cart.serviceMethod;
      if (!serviceMethod || serviceMethod === "dine-in") return null;

      const { date, time } = getOrderDateTime();
      const normalizedDate = normalizeDateString(date);
      const timezone = shop.value.timezone || "UTC";

      // Get restaurant operating hours
      const startTime = getRestaurantSetting("start_time") || "09:00";
      const endTime = getRestaurantSetting("end_time") || "22:00";

      // Check if there's a vacation override for this date
      const override = vacationSchedule.value.items.find(
        (item) => normalizeDateString(item.date) === normalizedDate,
      );

      if (override) {
        if (override.payload.type === "closed_all_day") {
          // Find the next day that's not closed
          const currentDate = new Date(normalizedDate);
          for (let i = 1; i <= 7; i++) {
            const nextDate = new Date(currentDate);
            nextDate.setDate(currentDate.getDate() + i);
            const nextDateStr = toLocalDateString(nextDate);
            const nextNormalizedDate = normalizeDateString(nextDateStr);

            const nextOverride = vacationSchedule.value.items.find(
              (item) => normalizeDateString(item.date) === nextNormalizedDate,
            );

            if (
              !nextOverride ||
              nextOverride.payload.type !== "closed_all_day"
            ) {
              // Check if there are intervals for this day
              if (nextOverride && nextOverride.payload.type === "intervals") {
                const firstInterval = nextOverride.payload.intervals[0];
                if (firstInterval) {
                  return {
                    date: nextDateStr,
                    time: firstInterval.start,
                  };
                }
              }
              // Use regular opening time
              return {
                date: nextDateStr,
                time: startTime,
              };
            }
          }
          return null;
        } else if (override.payload.type === "intervals") {
          // Find the next available interval
          const currentTime = time;
          const nextInterval = override.payload.intervals.find(
            (interval) => currentTime < interval.start,
          );

          if (nextInterval) {
            return {
              date,
              time: nextInterval.start,
            };
          }

          // No more intervals today, check next day
          const currentDate = new Date(normalizedDate);
          const nextDate = new Date(currentDate);
          nextDate.setDate(currentDate.getDate() + 1);
          const nextDateStr = toLocalDateString(nextDate);
          const nextNormalizedDate = normalizeDateString(nextDateStr);

          const nextOverride = vacationSchedule.value.items.find(
            (item) => normalizeDateString(item.date) === nextNormalizedDate,
          );

          if (nextOverride && nextOverride.payload.type === "intervals") {
            const firstInterval = nextOverride.payload.intervals[0];
            if (firstInterval) {
              return {
                date: nextDateStr,
                time: firstInterval.start,
              };
            }
          }

          return {
            date: nextDateStr,
            time: startTime,
          };
        }
      }

      // No vacation override - check if before opening or after closing
      if (time < startTime) {
        return {
          date,
          time: startTime,
        };
      } else if (time >= endTime) {
        // Next day at opening time
        const currentDate = new Date(normalizedDate);
        const nextDate = new Date(currentDate);
        nextDate.setDate(currentDate.getDate() + 1);
        const nextDateStr = toLocalDateString(nextDate);

        return {
          date: nextDateStr,
          time: startTime,
        };
      }

      return null;
    };

    const nextOpeningTime = computed(() => {
      if (!isVacationClosed.value) return null;
      return getNextOpeningTime();
    });

    const vacationClosureMessage = computed(() => {
      if (!isVacationClosed.value || !vacationOverride.value) return "";

      let baseMessage = "";
      if (vacationOverride.value.payload.type === "closed_all_day") {
        baseMessage = t("customer.checkout.closed_for_delivery_takeaway");
      } else {
        baseMessage = t("customer.checkout.outside_delivery_hours");
      }

      // Add next opening time if available
      if (nextOpeningTime.value) {
        const { date, time } = nextOpeningTime.value;
        const timezone = shop.value.timezone || "UTC";
        const currentDate = getTodayInTimezone(timezone);
        const currentDateStr = normalizeDateString(
          toLocalDateString(currentDate),
        );
        const openingDateStr = normalizeDateString(date);

        let dateLabel = "";
        if (currentDateStr === openingDateStr) {
          dateLabel = t("customer.checkout.today");
        } else {
          const tomorrow = new Date(currentDate);
          tomorrow.setDate(currentDate.getDate() + 1);
          const tomorrowStr = normalizeDateString(toLocalDateString(tomorrow));
          if (openingDateStr === tomorrowStr) {
            dateLabel = t("customer.checkout.tomorrow");
          } else {
            dateLabel = t("customer.checkout.on_date", [date]);
          }
        }

        return t("customer.checkout.next_available_message", [
          baseMessage,
          dateLabel,
          time,
        ]);
      }

      return baseMessage;
    });

    const handleCustomerPlaceOrder = async () => {
      isLoading.value = true;
      try {
        const tableId = groupOrder.value?.table_id
          ? groupOrder.value?.table_id
          : undefined;
        console.log("tableId", pp({ tableId }), realtime);
        if (!cart.cart.paymentMethod || !cart.cart.serviceMethod) {
          throw new Error();
        }
        const resp = await createCustomerCheckout(
          shop.value.slug,
          {
            ...pp(cart.cart),
            tableId: tableId,
          },
          cart.cart.serviceMethod,
          cart.cart.paymentMethod,
          tableId ? "grouped" : "single",
          groupOrder.value?.id,
          tableId,
        );
        if (cart.cart.paymentMethod === "online") {
          await createIntegratedWorkflowMutation.mutateAsync(resp.id);

          router.push(
            `/restaurant/${shop.value?.slug}/checkout/${resp.id}/payment-status`,
          );
          cart.reset();
        } else {
          // Redirect to checkout status page
          cart.reset();
          router.push(
            `/restaurant/${shop.value?.slug}/group-order/${groupOrder.value?.id}/checkout-status/${resp.id}`,
          );
        }
      } catch (error) {
        console.error("Failed to create checkout:", error);
        isLoading.value = false;
      } finally {
        isLoading.value = false;
      }
    };

    return () => {
      console.log("validate", !validateCart(cart, shop.value));
      const tableId = groupOrder.value?.table_id
        ? groupOrder.value?.table_id
        : undefined;

      if (cart.cart.items.length === 0) {
        return (
          <div class="flex flex-col items-center justify-center p-8 text-center">
            <div class="mb-4">
              <span class="material-symbols-outlined text-6xl text-gray-400">
                shopping_cart
              </span>
            </div>
            <h3 class="text-xl font-semibold mb-2">
              {t("customer.checkout.cart_empty")}
            </h3>
            <p class="text-gray-600 mb-6">
              {t("customer.checkout.cart_empty_copy")}
            </p>
            <RouterLink
              to={`/restaurant/${shop.value?.slug || ""}/group-order/${
                groupOrder.value?.id
              }`}
              class="btn btn-primary"
            >
              <span class="material-symbols-outlined mr-2">restaurant</span>
              {t("customer.checkout.browse_menu")}
            </RouterLink>
          </div>
        );
      }

      return (
        <div class="px-4 py-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div class="space-y-4">
              <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                <CartItems />
              </div>
              <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                <NotesSection />
              </div>
              {tableId ? <></> : null}
              {cart.cart.serviceMethod === "delivery" ? (
                <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                  <DeliverySection
                    show={cart.cart.serviceMethod === "delivery"}
                  />
                </div>
              ) : null}
              {cart.cart.serviceMethod === "takeaway" ? (
                <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                  <PickupDetailsSection
                    show={cart.cart.serviceMethod === "takeaway"}
                  />
                </div>
              ) : null}
            </div>

            {/* Right Column */}
            <div class="space-y-4">
              <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                <BillSection />
              </div>
              <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                <TipsSection />
              </div>
              {tableId ? null : (
                <>
                  <div class="p-2 bg-base-100 rounded-xl shadow-xl border hidden">
                    <ScheduleSection />
                  </div>
                </>
              )}
              <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                <PaymentSection />
              </div>
              <div class="p-2 bg-base-100 rounded-xl shadow-xl border">
                <div class="p-2 lg:p-4">
                  <LegalDisclamair />
                </div>
              </div>
              <div class="hidden lg:block md:mt-auto">
                {vacationClosureMessage.value && (
                  <div class="alert alert-warning mb-3">
                    <span class="material-symbols-outlined">schedule</span>
                    <span>{vacationClosureMessage.value}</span>
                  </div>
                )}
                <button
                  class={`btn btn-lg btn-primary btn-block shadow-xl ${
                    isLoading.value ? "" : ""
                  }`}
                  onClick={handleCustomerPlaceOrder}
                  disabled={
                    isLoading.value ||
                    !validateCart(cart, shop.value) ||
                    isVacationClosed.value
                  }
                >
                  {isLoading.value ? (
                    <span class="loading loading-spinner loading-xs" />
                  ) : (
                    t("customer.checkout.place_order")
                  )}
                </button>
              </div>
            </div>
            <div class="sticky bottom-4 lg:hidden">
              {vacationClosureMessage.value && (
                <div class="alert alert-warning mb-3">
                  <span class="material-symbols-outlined">schedule</span>
                  <span>{vacationClosureMessage.value}</span>
                </div>
              )}
              <button
                class={`btn btn-lg btn-primary btn-block shadow-xl ${
                  isLoading.value ? "" : ""
                }`}
                onClick={handleCustomerPlaceOrder}
                disabled={
                  isLoading.value ||
                  !validateCart(cart, shop.value) ||
                  isVacationClosed.value
                }
              >
                {isLoading.value ? (
                  <span class="loading loading-spinner loading-xs" />
                ) : (
                  t("customer.checkout.place_order")
                )}
              </button>
            </div>
          </div>
        </div>
      );
    };
  },
});

export const validateCart = (cart: CartAPI, shop?: any) => {
  if (cart.cart.items.length === 0) return false;
  if (!cart.cart.serviceMethod) return false;
  if (cart.cart.serviceMethod === "dine-in") {
  } else if (cart.cart.serviceMethod === "takeaway") {
    if (
      !cart.cart.pickupDetails ||
      !cart.cart.pickupDetails.name ||
      !cart.cart.pickupDetails.email ||
      !cart.cart.pickupDetails.phone
    )
      return false;
  } else if (cart.cart.serviceMethod === "delivery") {
    if (!cart.cart.addressId || !cart.cart.selectedAddress) return false;

    // Check minimum order requirement for delivery
    if (shop.value && cart.cart.selectedAddress) {
      const deliveryCities = getDeliveryCities(shop.value.settings || []);
      const selectedCity = deliveryCities.find(
        (city) =>
          city.name.toLowerCase() ===
          cart.cart.selectedAddress?.city.toLowerCase(),
      );

      if (selectedCity && selectedCity.minimum) {
        const minimumAmount = parseFloat(selectedCity.minimum);
        const cartSubtotal = calculateCartSubtotal(cart.cart);
        if (cartSubtotal < minimumAmount) return false;
      }
    }
  }
  return true;
};
